/**
 * In-memory per-tab event ring + fingerprint deduplication.
 *
 * The store is intentionally a plain mutable object that components observe
 * via a tiny subscription pattern (no Zustand here — events are scoped to
 * one tab and we want to avoid a cross-tab global). Each tab body creates
 * its own `EventStore` instance and tears it down on unmount.
 */
import type { LogEvent } from './eventDetector';

export interface EventBucket {
  rep: LogEvent;
  count: number;
  lastTs: string;
  /** Append-only list of additional event ids (besides `rep.id`) that
   * folded into this bucket — useful if we ever want "show all 5". */
  alsoIds: string[];
}

const CAP = 50;

export class EventStore {
  private buckets: EventBucket[] = [];
  private byFp = new Map<string, EventBucket>();
  private unread = 0;
  private listeners = new Set<() => void>();
  private notifyPending = false;

  push(events: LogEvent[]): void {
    if (events.length === 0) return;
    let changed = false;
    for (const ev of events) {
      const existing = this.byFp.get(ev.fingerprint);
      if (existing) {
        existing.count++;
        existing.lastTs = ev.ts;
        existing.alsoIds.push(ev.id);
        // Move bucket to front (most-recent first).
        const i = this.buckets.indexOf(existing);
        if (i > 0) {
          this.buckets.splice(i, 1);
          this.buckets.unshift(existing);
        }
        this.unread++;
        changed = true;
      } else {
        const bucket: EventBucket = { rep: ev, count: 1, lastTs: ev.ts, alsoIds: [] };
        this.buckets.unshift(bucket);
        this.byFp.set(ev.fingerprint, bucket);
        this.unread++;
        changed = true;
      }
    }
    // Evict from the tail when over cap.
    while (this.buckets.length > CAP) {
      const dropped = this.buckets.pop();
      if (dropped) this.byFp.delete(dropped.rep.fingerprint);
    }
    if (changed) this.scheduleNotify();
  }

  markRead(): void {
    if (this.unread === 0) return;
    this.unread = 0;
    this.scheduleNotify();
  }

  clearAll(): void {
    if (this.buckets.length === 0 && this.unread === 0) return;
    this.buckets = [];
    this.byFp.clear();
    this.unread = 0;
    this.scheduleNotify();
  }

  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  snapshot(): { buckets: readonly EventBucket[]; unread: number } {
    return { buckets: this.buckets, unread: this.unread };
  }

  private scheduleNotify(): void {
    if (this.notifyPending) return;
    this.notifyPending = true;
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (cb: FrameRequestCallback) => setTimeout(() => cb(Date.now()), 16);
    raf(() => {
      this.notifyPending = false;
      for (const l of this.listeners) l();
    });
  }
}
